import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Target, Flame } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAwardBadges } from '../hooks/useAwardBadges';
import GemIcon from '@/components/icons/GemIcon';
import { gamificationServiceApi } from '@/services/api';
import { getMe } from '@/services/authService';

const toPersianDigits = (num: number | string) => String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

interface GoalsRewardPageState {
  previous_level: number; previous_level_xp: number; previous_next_level_xp: number;
  level: number; current_level_xp: number; next_level_xp: number;
  received_gems?: number | null; received_xp?: number | null;
  daily_goal: number; streak_goal: number; daily_goal_gems?: number | null; daily_goal_xp?: number | null;
  streak_goal_gems?: number | null; streak_goal_xp?: number | null;
  today_minutes: number; previous_minutes: number; streak_days: number; previous_streak: number;
  initialGems?: number; points?: number | null;
}

const GoalsRewardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const awardBadgesMutation = useAwardBadges();
  const state = (location.state || {}) as GoalsRewardPageState;
  const {
    previous_level = 1, previous_level_xp = 0, previous_next_level_xp = 150,
    level = 1, current_level_xp = 0, next_level_xp = 150, received_gems = 0, received_xp = 0,
    daily_goal = 10, streak_goal = 3, daily_goal_gems = 0, daily_goal_xp = 0,
    streak_goal_gems = 0, streak_goal_xp = 0, today_minutes = 0, previous_minutes = 0,
    streak_days = 0, previous_streak = 0, initialGems = 0, points = 0,
  } = state;

  const [showContinueButton, setShowContinueButton] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [displayedGems, setDisplayedGems] = useState(initialGems);
  const [displayedLevel, setDisplayedLevel] = useState(previous_level);
  const [displayedLevelXp, setDisplayedLevelXp] = useState(previous_level_xp);
  const [displayedNextLevelXp, setDisplayedNextLevelXp] = useState(previous_next_level_xp);
  const [levelProgress, setLevelProgress] = useState(previous_next_level_xp > 0 ? (previous_level_xp / previous_next_level_xp) * 100 : 0);
  const [dailyProgress, setDailyProgress] = useState(daily_goal > 0 ? Math.min((previous_minutes / daily_goal) * 100, 100) : 0);
  const [streakProgress, setStreakProgress] = useState(streak_goal > 0 ? Math.min((previous_streak / streak_goal) * 100, 100) : 0);
  const [valuesAnimated, setValuesAnimated] = useState(false);
  const [levelUpAnimating, setLevelUpAnimating] = useState(false);
  const startedRef = useRef(false);
  const currentXpRef = useRef(previous_level_xp);
  const currentLevelRef = useRef(previous_level);
  const currentNextXpRef = useRef(previous_next_level_xp);
  const currentGemsRef = useRef(initialGems);

  const animateGems = (amount: number) => new Promise<void>((resolve) => {
    if (!amount || amount <= 0) return resolve();
    const start = currentGemsRef.current;
    const steps = 16;
    let step = 0;
    const timer = window.setInterval(() => {
      step += 1;
      setDisplayedGems(Math.round(start + amount * (step / steps)));
      if (step >= steps) {
        currentGemsRef.current = start + amount;
        window.clearInterval(timer);
        resolve();
      }
    }, 18);
  });

  const animateXp = async (amount: number) => {
    if (!amount || amount <= 0) return;
    const newXp = currentXpRef.current + amount;
    const didLevelUp = newXp >= currentNextXpRef.current && currentLevelRef.current < level;
    if (didLevelUp) {
      currentLevelRef.current = level;
      currentNextXpRef.current = next_level_xp;
      currentXpRef.current = Math.max(0, newXp - previous_next_level_xp) + Number(received_xp || 0);
      setLevelProgress(100);
      await new Promise((resolve) => window.setTimeout(resolve, 220));
      setLevelUpAnimating(true);
      setDisplayedLevel(level);
      setDisplayedLevelXp(currentXpRef.current);
      setDisplayedNextLevelXp(next_level_xp);
      await new Promise((resolve) => window.setTimeout(resolve, 260));
      setLevelUpAnimating(false);
      await animateGems(Number(received_gems) || 0);
    } else {
      currentXpRef.current = newXp;
      setDisplayedLevelXp(newXp);
      setLevelProgress(Math.min((newXp / currentNextXpRef.current) * 100, 100));
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const run = async () => {
      await new Promise((resolve) => window.setTimeout(resolve, 120));
      await animateXp(Number(points) || 0);
      setValuesAnimated(true);
      setDailyProgress(daily_goal > 0 ? Math.min((today_minutes / daily_goal) * 100, 100) : 0);
      await new Promise((resolve) => window.setTimeout(resolve, 360));
      if (today_minutes >= daily_goal) {
        await animateXp(Number(daily_goal_xp) || 0);
        await animateGems(Number(daily_goal_gems) || 0);
      }
      setStreakProgress(streak_goal > 0 ? Math.min((streak_days / streak_goal) * 100, 100) : 0);
      await new Promise((resolve) => window.setTimeout(resolve, 360));
      if (streak_days >= streak_goal) {
        await animateXp(Number(streak_goal_xp) || 0);
        await animateGems(Number(streak_goal_gems) || 0);
      }
      setDisplayedLevelXp(current_level_xp);
      setLevelProgress(next_level_xp > 0 ? Math.min((current_level_xp / next_level_xp) * 100, 100) : 0);
      setDisplayedLevel(level);
      setDisplayedNextLevelXp(next_level_xp);
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      setShowContinueButton(true);
    };
    void run();
  }, []);

  const lastLearningPathBookId = localStorage.getItem('lastLearningPathBookId');
  const bookId = lastLearningPathBookId ? parseInt(lastLearningPathBookId, 10) : null;
  const navigateToLearningPath = () => {
    if (bookId) {
      queryClient.invalidateQueries({ queryKey: ['my-sections', bookId] });
      queryClient.invalidateQueries({ queryKey: ['book-detail', bookId] });
    }
    queryClient.invalidateQueries({ queryKey: ['user-library'] });
    queryClient.invalidateQueries({ queryKey: ['quests'] });
    navigate(bookId ? '/learning/path' : '/dashboard', { state: bookId ? { bookId, showReferralInvite: true } : undefined, replace: true });
  };

  const handleContinue = async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const awardResult = await awardBadgesMutation.mutateAsync();
      if (awardResult?.count > 0 && awardResult?.newBadges?.length > 0) {
        navigate('/learning/achievement', { state: { awardResult }, replace: true });
        return;
      }
      const meRes = await getMe();
      const response = await gamificationServiceApi.get('/challenges/updates');
      const payload = response.data?.data;
      const challenges = payload?.challenges || [];
      const hasProgressChange = challenges.some((challenge: any) => challenge.previousProgress !== challenge.currentProgress);
      if (hasProgressChange) {
        navigate('/learning/challenges', { state: { challenges, level_info: payload?.level_info || null, book_id: payload?.book_id || null, initialGems: meRes?.gems || 0 }, replace: true });
      } else navigateToLearningPath();
    } catch {
      navigateToLearningPath();
    }
  };

  const dailyStart = daily_goal > 0 ? Math.min((previous_minutes / daily_goal) * 100, 100) : 0;
  const streakStart = streak_goal > 0 ? Math.min((previous_streak / streak_goal) * 100, 100) : 0;

  return (
    <div className="min-h-[100dvh] bg-[#fafafa] text-gray-800" dir="rtl">
      <div className="container mx-auto flex min-h-[100dvh] w-full max-w-md flex-col px-6 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <div className="mb-7 flex items-center justify-between gap-4">
          <div className="relative flex h-[30px] flex-1 items-center gap-1" style={{ direction: 'ltr' }}>
            <div className="relative h-full flex-1 overflow-visible rounded-full border-2 border-white bg-gray-200 shadow-md">
              <div className="absolute bottom-0 right-0 top-0 rounded-full transition-[width] duration-[250ms] ease-out" style={{ width: `${levelProgress}%`, backgroundColor: '#FBBF24', boxShadow: '0 0 6px 1px rgba(251, 191, 36, 0.4)' }} />
              <div className="relative z-10 flex h-full items-center justify-center"><span dir="ltr" className="pt-0.5 text-xs font-bold leading-none text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] sm:text-sm">{toPersianDigits(displayedNextLevelXp)} / {toPersianDigits(displayedLevelXp)}</span></div>
            </div>
            <motion.div className="relative z-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#F59E0B] bg-[#FBBF24] shadow-md sm:h-[42px] sm:w-[42px]" animate={levelUpAnimating ? { scale: [1, 1.25, 1] } : {}} transition={{ duration: 0.3 }}>
              <span className="text-sm font-black text-white drop-shadow-sm sm:text-base">{toPersianDigits(displayedLevel)}</span>
            </motion.div>
          </div>
          <div className="inline-flex h-9 shrink-0 items-center gap-1 rounded-3xl bg-cyan-500 px-2 text-white shadow-md shadow-cyan-500/30 sm:h-10">
            <span className="flex size-5 items-center justify-center"><GemIcon size={18} color="white" /></span>
            <span className="relative top-[1.5px] min-w-[2ch] text-center text-[13px] font-black leading-none sm:text-sm">{toPersianDigits(displayedGems)}</span>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-center gap-4 px-4">
          <GoalCard title="هدف روزانه" icon={<Target className="h-4 w-4 text-[#6A5BE2]" />} iconClass="bg-[#6A5BE2]/10" value={valuesAnimated ? today_minutes : previous_minutes} unit="دقیقه" label="هدف مطالعه روزانه" target={daily_goal} progress={dailyProgress} initialProgress={dailyStart} color="#6A5BE2" />
          <GoalCard title="هدف پیوستگی" icon={<Flame className="h-4 w-4 text-[#F97316]" />} iconClass="bg-[#F97316]/10" value={valuesAnimated ? streak_days : previous_streak} unit="روز" label="هدف پیوستگی" target={streak_goal} progress={streakProgress} initialProgress={streakStart} color="#F97316" />
        </div>

        <motion.button initial={{ opacity: 0, y: 18 }} animate={{ opacity: showContinueButton ? 1 : 0, y: showContinueButton ? 0 : 18 }} transition={{ duration: 0.25 }} onClick={handleContinue} disabled={isNavigating || !showContinueButton} className="mx-auto mt-5 flex w-[84%] max-w-[350px] items-center justify-center gap-3 rounded-xl bg-[#6e61ff] py-3.5 text-center text-[15px] font-bold text-white shadow-xl transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 hover:bg-[#5a4ed9]">
          {isNavigating && <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />}<span>ادامه</span>
        </motion.button>
      </div>
    </div>
  );
};

interface GoalCardProps { title: string; icon: React.ReactNode; iconClass: string; value: number; unit: string; label: string; target: number; progress: number; initialProgress: number; color: string; }

const GoalCard: React.FC<GoalCardProps> = ({ title, icon, iconClass, value, unit, label, target, progress, initialProgress, color }) => (
  <div className="relative overflow-visible rounded-3xl bg-[#FFFDFC] p-4 ring-1 ring-black/10 sm:p-5" style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}>
    <div className="mb-3 flex items-center gap-2"><div className={`rounded-lg p-1.5 shadow-sm ${iconClass}`}>{icon}</div><h3 className="font-persian text-xs font-bold text-black sm:text-sm">{title}</h3></div>
    <div className="mb-4 flex items-baseline gap-1"><span className="text-3xl font-bold tracking-tight text-black sm:text-4xl">{toPersianDigits(value)}</span><span className="font-persian text-sm font-medium text-black sm:text-base">{unit}</span></div>
    <div className="space-y-2"><div className="flex items-center justify-between px-0.5 text-xs sm:text-sm"><span className="font-persian font-medium text-black">{label}</span><span className="font-persian font-bold text-black">{toPersianDigits(target)} {unit}</span></div><div className="relative h-5 overflow-hidden rounded-full shadow-inner" style={{ backgroundColor: `${color}26` }}><motion.div className="absolute bottom-0 right-0 top-0 rounded-full shadow-sm" initial={{ width: `${initialProgress}%` }} animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease: 'easeOut' }} style={{ backgroundColor: color }} /></div></div>
  </div>
);

export default GoalsRewardPage;
